import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { Star } from 'lucide-react';
import { GaleriaAlumnos } from './GaleriaAlumnos';
const testimonios = [{
  nombre: 'Martín G.',
  foto: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=200&auto=format&fit=crop',
  texto: 'Pensé que iba a sentir miedo al respirar bajo el agua, pero la forma de enseñar me hizo sentir seguro desde la primera clase.'
}, {
  nombre: 'Lucía R.',
  foto: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop',
  texto: 'La piscina de 4 metros y las clases extra hicieron una diferencia enorme cuando llegó el momento de bucear en aguas abiertas.'
}, {
  nombre: 'Sebastián M.',
  foto: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop',
  texto: 'Lo que más valoro es la atención personalizada. Nunca sentí que era un alumno más.'
}, {
  nombre: 'Carla A.',
  foto: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200&auto=format&fit=crop',
  texto: 'Obtener la certificación fue increíble, pero lo mejor fue descubrir una nueva forma de viajar y conocer personas.'
}, {
  nombre: 'Pablo C.',
  foto: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?q=80&w=200&auto=format&fit=crop',
  texto: 'Comencé sin experiencia y terminé planificando mis próximas vacaciones de buceo.'
}, {
  nombre: 'Florencia D.',
  foto: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&auto=format&fit=crop',
  texto: 'Los instructores son verdaderos profesionales. Me transmitieron seguridad en cada paso y nunca me sentí apurada.'
}, {
  nombre: 'Nicolás B.',
  foto: 'https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=200&auto=format&fit=crop',
  texto: 'Mi primera experiencia buceando fue inolvidable. Con la certificación internacional ya buceé en dos países distintos.'
}, {
  nombre: 'Valentina S.',
  foto: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop',
  texto: 'Tenía mucho miedo al principio, pero la confianza que generan hace que todo fluya. Hoy no puedo dejar de bucear.'
}];
const SLIDE_FRACTION = 0.85; // each slide takes 85% of viewport width -> peek next on the right
export function Testimonios() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [slideWidth, setSlideWidth] = useState(0);
  const [current, setCurrent] = useState(0);
  const x = useMotionValue(0);
  const pausedRef = useRef(false);
  // Measure slide width responsively
  useEffect(() => {
    const measure = () => {
      if (viewportRef.current) {
        setSlideWidth(viewportRef.current.offsetWidth * SLIDE_FRACTION);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  // Animate to current slide
  useEffect(() => {
    const controls = animate(x, -current * slideWidth, {
      type: 'spring',
      stiffness: 260,
      damping: 32
    });
    return controls.stop;
  }, [current, slideWidth, x]);
  // Autoplay every 4s, infinite loop
  useEffect(() => {
    const id = setInterval(() => {
      if (!pausedRef.current) {
        setCurrent(prev => (prev + 1) % testimonios.length);
      }
    }, 4000);
    return () => clearInterval(id);
  }, []);
  const goTo = (i: number) => {
    setCurrent((i % testimonios.length + testimonios.length) % testimonios.length);
  };
  const handleDragEnd = (_: unknown, info: {
    offset: {
      x: number;
    };
    velocity: {
      x: number;
    };
  }) => {
    const threshold = slideWidth * 0.2;
    if (info.offset.x < -threshold || info.velocity.x < -400) {
      goTo(current + 1);
    } else if (info.offset.x > threshold || info.velocity.x > 400) {
      goTo(current - 1);
    } else {
      animate(x, -current * slideWidth, {
        type: 'spring',
        stiffness: 260,
        damping: 32
      });
    }
  };
  return <section className="py-24 px-6 bg-brand-background relative overflow-hidden" data-id="element-300">
      <div className="max-w-6xl mx-auto" data-id="element-301">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12 sm:mb-16 max-w-2xl mx-auto" data-id="element-302">
          <h3 className="text-white font-heading font-bold text-3xl sm:text-4xl md:text-5xl uppercase tracking-tight mb-4" data-id="element-303">
            Nuestros alumnos
          </h3>
          <p className="text-brand-details/90 text-lg font-light" data-id="element-304">
            Más de 4.000 personas ya dieron su primera respiración bajo el agua
            con nosotros.
          </p>
        </motion.div>

        {/* Swipeable students gallery */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.7
      }} data-id="element-305">
          <GaleriaAlumnos data-id="element-306" />
        </motion.div>

        {/* Inspirational phrase */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6
      }} className="text-center text-white/80 text-lg sm:text-xl font-light italic max-w-2xl mx-auto mt-8 mb-14 sm:mb-16 leading-relaxed" data-id="element-307">
          Más de 4.000 alumnos ya dieron su primera respiración bajo el agua con
          nosotros.
        </motion.p>

        {/* Carousel viewport */}
        <div ref={viewportRef} className="overflow-hidden cursor-grab active:cursor-grabbing" onMouseEnter={() => pausedRef.current = true} onMouseLeave={() => pausedRef.current = false} onTouchStart={() => pausedRef.current = true} onTouchEnd={() => pausedRef.current = false} data-id="element-308">
          <motion.div className="flex" style={{
          x
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.15} onDragStart={() => pausedRef.current = true} onDragEnd={handleDragEnd} data-id="element-309">
            {testimonios.map((t, index) => {
            const isActive = current === index;
            return <div key={index} className="shrink-0 px-2" style={{
              width: slideWidth || '85%'
            }} data-id="element-310">
                  <figure className={`h-full select-none rounded-3xl border bg-brand-primary/5 backdrop-blur-md p-8 flex flex-col items-center text-center transition-all duration-500 ${isActive ? 'border-brand-primary/50 shadow-[0_18px_55px_-18px_rgba(0,184,217,0.55)] opacity-100 scale-100' : 'border-brand-primary/15 opacity-50 scale-[0.93]'}`} data-id="element-311">
                    <img src={t.foto} alt={`Foto de ${t.nombre}`} draggable={false} className="w-16 h-16 rounded-full object-cover border-2 border-brand-primary/50 mb-4 pointer-events-none" data-id="element-312" />
                    <div className="flex gap-1 mb-5 text-brand-primary" aria-label="5 de 5 estrellas" data-id="element-313">
                      {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="currentColor" data-id="element-314" />)}
                    </div>
                    <blockquote className="text-white/90 italic leading-relaxed flex-grow" data-id="element-315">
                      “{t.texto}”
                    </blockquote>
                    <figcaption className="text-brand-primary font-bold tracking-wide mt-6" data-id="element-316">
                      — {t.nombre}
                    </figcaption>
                  </figure>
                </div>;
          })}
          </motion.div>
        </div>

        {/* Dots indicators */}
        <div className="flex justify-center gap-2 mt-10" data-id="element-317">
          {testimonios.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir al testimonio ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${current === i ? 'w-8 bg-brand-primary' : 'w-2 bg-brand-primary/30 hover:bg-brand-primary/60'}`} data-id="element-318" />)}
        </div>

        {/* Final highlighted phrase */}
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6
      }} className="text-center text-brand-primary font-heading font-bold text-xl sm:text-2xl tracking-tight mt-16 max-w-2xl mx-auto" data-id="element-319">
          La confianza se construye alumno por alumno desde 2014.
        </motion.p>

        {/* External social proof */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6
      }} className="text-center mt-20 max-w-2xl mx-auto" data-id="element-320">
          <h4 className="text-white font-heading font-bold text-2xl sm:text-3xl uppercase tracking-tight mb-4" data-id="element-321">
            ¿Querés ver más experiencias reales?
          </h4>
          <div className="space-y-3 text-white/80 text-lg font-light mb-8" data-id="element-322">
            <p data-id="element-323">
              Miles de alumnos ya dieron su primera respiración bajo el agua con
              nosotros.
            </p>
            <p data-id="element-324">
              Podés leer reseñas verificadas y conocer las experiencias reales
              de quienes ya realizaron su curso de buceo y obtuvieron su
              certificación.
            </p>
          </div>
          <div className="flex items-center justify-center gap-1 mb-2" data-id="element-325">
            {Array.from({
            length: 5
          }).map((_, i) => <Star key={i} size={22} className="text-amber-400 fill-amber-400" data-id="element-326" />)}
          </div>
          <p className="text-white/80 text-sm sm:text-base mb-6" data-id="element-327">
            4.9/5 basado en las reseñas de nuestros alumnos
          </p>

          <a href="https://maps.app.goo.gl/K7BJtcQaXmaEyoLQA?g_st=ic" target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-[#00B8D9] to-[#4FC3F7] text-[#031D33] font-bold uppercase tracking-widest text-sm py-3 px-7 rounded-full transition-all duration-300 transform hover:scale-[1.03] active:scale-[0.98] shadow-[0_10px_40px_-5px_rgba(0,184,217,0.5)] hover:shadow-[0_16px_55px_-5px_rgba(0,184,217,0.75)]" data-id="element-328">
            Ver más reseñas
          </a>
        </motion.div>
      </div>
    </section>;
}